import { openSettings } from "expo-linking";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import type { AlertButton } from "react-native";
import { Alert } from "react-native";

import { isWeb } from "@/utils/platform.util";

const OpenSettingsButton: AlertButton[] = [
  {
    isPreferred: true,
    onPress: () => void openSettings(),
    text: "Open settings",
  },
  {
    isPreferred: false,
    style: "cancel",
    text: "Cancel",
  },
];

const permissionDeniedAlert = (permissionName: string) => {
  Alert.alert(
    `${permissionName} permissions denied`,
    `You need to enable ${permissionName} permissions in your app settings`,
    OpenSettingsButton,
  );
};

const convertAssetToFile = async ({
  file,
  fileName,
}: {
  file: FilePickerAsset;
  fileName: string;
}): Promise<File> => {
  // populated by expo-image-picker / expo-document-picker on web only, with the actual file blob
  if (isWeb() && !isNil(file.file)) {
    return new File([file.file], fileName, { type: file.mimeType });
  }
  const response = await fetch(file.uri);
  const blob = await response.blob();
  return new File([blob], fileName, {
    type: file.mimeType,
  });
};

/**
 * Creates a timeout promise that can either resolve or reject after a specified duration
 *
 * @param timeoutMs - Duration in milliseconds before the timeout triggers
 * @param shouldReject - If true, rejects with an error. If false, resolves with resolveValue
 * @param resolveValue - Value to resolve with when shouldReject is false
 * @returns Promise that resolves or rejects after the specified duration
 */
const createTimeoutPromise = <T>({
  resolveValue,
  shouldReject = true,
  timeoutMs,
}: {
  resolveValue: T;
  shouldReject?: boolean;
  timeoutMs: number;
}): Promise<T> =>
  // eslint-disable-next-line max-params -- Promise constructor requires resolve and reject params
  new Promise((resolve, reject) => {
    setTimeout(() => {
      if (shouldReject) {
        reject(new Error(`Operation timed out after ${timeoutMs}ms`));
      } else {
        resolve(resolveValue);
      }
    }, timeoutMs);
  });

// isNotNilNotEmpty is a compromise due to the reality of typescript's typing system.
//  just like lodash's isEmpty, there is no way to provide correct type safety via a isNilOrEmpty function.
//  typing isNilOrEmpty as 'x is Nullable<T>' would be incorrect,
//  as isNilOrEmpty(null) === true is indeed nullable, but isNilOrEmpty([]) === true is NOT nullable
//  so we settle instead of a type predicate of 'x is NonNullable<T>'.
//  similarly, defining a isNilOrEmpty = !isNotNilNotEmpty(x) would be trivial logically,
//  but have the same incomplete typing, and cause typescript to complain about unhandled null/undefined values.
const isNotNilNotEmpty = <T>(x: T): x is NonNullable<T> =>
  !isNil(x) && !isEmpty(x);

export {
  convertAssetToFile,
  createTimeoutPromise,
  isNotNilNotEmpty,
  permissionDeniedAlert,
};
